import React, { useState, useEffect, useRef } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { HexColorPicker } from 'react-colorful';

// Register custom code block format
const CodeBlock = Quill.import('formats/code-block');
class CustomCodeBlock extends CodeBlock {
  static create(value) {
    let node = super.create(value);
    if (value && value !== true) {
      node.setAttribute('data-language', value);
      node.classList.add(`language-${value}`);
    }
    return node;
  }

  static formats(node) {
    return node.getAttribute('data-language') || true;
  }
}
Quill.register(CustomCodeBlock, true);

// Keep regular inline code separate
const Code = Quill.import('formats/code');
Quill.register(Code, true);

const Editor = ({ 
  value = '', 
  onChange, 
  placeholder = "Start writing...", 
  minHeight = '400px',
  className = '',
  style = {},
  ...props 
}) => {
  const [showTextColorPicker, setShowTextColorPicker] = useState(false);
  const [showBackgroundColorPicker, setShowBackgroundColorPicker] = useState(false);
  const [textColor, setTextColor] = useState('#000000');
  const [backgroundColor, setBackgroundColor] = useState('#ffff00');
  const [textColorHex, setTextColorHex] = useState('#000000');
  const [backgroundColorHex, setBackgroundColorHex] = useState('#ffff00');
  const quillRef = useRef(null);
  const textColorRef = useRef(null);
  const backgroundColorRef = useRef(null);

  // Close color pickers when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (textColorRef.current && !textColorRef.current.contains(event.target)) {
        setShowTextColorPicker(false);
      }
      if (backgroundColorRef.current && !backgroundColorRef.current.contains(event.target)) {
        setShowBackgroundColorPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Apply text color
  const applyTextColor = (color) => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection(true);
      if (range) {
        if (range.length === 0) {
          quill.format('color', color);
        } else {
          quill.formatText(range.index, range.length, 'color', color);
          quill.setSelection(range.index, range.length);
        }
      } else {
        quill.format('color', color);
      }
    }
  };

  // Apply background color
  const applyBackgroundColor = (color) => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection(true);
      if (range) {
        if (range.length === 0) {
          quill.format('background', color);
        } else {
          quill.formatText(range.index, range.length, 'background', color);
          quill.setSelection(range.index, range.length);
        }
      } else {
        quill.format('background', color);
      }
    }
  };

  // Handle text color change
  const handleTextColorChange = (color) => {
    setTextColor(color);
    setTextColorHex(color);
    applyTextColor(color);
  };

  // Handle background color change
  const handleBackgroundColorChange = (color) => {
    setBackgroundColor(color);
    setBackgroundColorHex(color);
    applyBackgroundColor(color);
  };

  // Handle hex input for text color
  const handleTextColorHexChange = (e) => {
    const value = e.target.value;
    setTextColorHex(value);
    if (/^#[0-9A-F]{6}$/i.test(value)) {
      setTextColor(value);
      applyTextColor(value);
    }
  };

  // Handle hex input for background color
  const handleBackgroundColorHexChange = (e) => {
    const value = e.target.value;
    setBackgroundColorHex(value);
    if (/^#[0-9A-F]{6}$/i.test(value)) {
      setBackgroundColor(value);
      applyBackgroundColor(value);
    }
  };

  // Handle hex input key press
  const handleHexKeyPress = (e, isTextColor) => {
    if (e.key === 'Enter') {
      const value = e.target.value;
      if (/^#[0-9A-F]{6}$/i.test(value)) {
        if (isTextColor) {
          setTextColor(value);
          applyTextColor(value);
        } else {
          setBackgroundColor(value);
          applyBackgroundColor(value);
        }
      } else {
        if (isTextColor) {
          setTextColorHex(textColor);
        } else {
          setBackgroundColorHex(backgroundColor);
        }
      }
    }
  };

  // Custom toolbar setup after component mounts - EXACT copy from working version
  useEffect(() => {
    const setupCustomToolbar = () => {
      const toolbar = document.querySelector('.ql-toolbar');
      if (toolbar) {
        const textColorPicker = toolbar.querySelector('.ql-color');
        const backgroundColorPicker = toolbar.querySelector('.ql-background');
        
        if (textColorPicker) {
          // Only hide the color picker dropdown, not other dropdowns
          const textDropdown = textColorPicker.querySelector('.ql-picker-options');
          if (textDropdown) textDropdown.style.display = 'none';
          
          const textColorButton = textColorPicker.querySelector('.ql-picker-label');
          if (textColorButton) {
            // Remove any existing listeners first
            textColorButton.replaceWith(textColorButton.cloneNode(true));
            const newTextColorButton = textColorPicker.querySelector('.ql-picker-label');
            
            newTextColorButton.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowTextColorPicker(!showTextColorPicker);
              setShowBackgroundColorPicker(false);
            });
          }
        }

        if (backgroundColorPicker) {
          // Only hide the background color picker dropdown, not other dropdowns
          const backgroundDropdown = backgroundColorPicker.querySelector('.ql-picker-options');
          if (backgroundDropdown) backgroundDropdown.style.display = 'none';
          
          const backgroundColorButton = backgroundColorPicker.querySelector('.ql-picker-label');
          if (backgroundColorButton) {
            // Remove any existing listeners first
            backgroundColorButton.replaceWith(backgroundColorButton.cloneNode(true));
            const newBackgroundColorButton = backgroundColorPicker.querySelector('.ql-picker-label');
            
            newBackgroundColorButton.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowBackgroundColorPicker(!showBackgroundColorPicker);
              setShowTextColorPicker(false);
            });
          }
        }
      }
    };

    const timer = setTimeout(setupCustomToolbar, 100);
    return () => clearTimeout(timer);
  }, [showTextColorPicker, showBackgroundColorPicker]);

  // Enhanced editor modules with better toolbar - EXACT copy from working version
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'code-block': [
        '', 'javascript', 'typescript', 'jsx', 'tsx', 'python', 'java', 'csharp', 'cpp', 'c',
        'php', 'ruby', 'go', 'rust', 'swift', 'kotlin', 'dart', 'html', 'css', 'scss',
        'sql', 'bash', 'json', 'xml', 'yaml', 'text'
      ] }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link', 'blockquote'],
      [{ 'align': [] }],
      ['clean']
    ],
  };

  const formats = [
    'header', 'bold', 'italic', 'underline', 'strike', 'code-block',
    'color', 'background', 'list', 'bullet', 'indent',
    'link', 'blockquote', 'align'
  ];

  return (
    <>
      <div 
        className={`border border-gray-300 rounded-lg overflow-hidden ${className}`} 
        style={{ position: 'relative', ...style }}
      >
        <ReactQuill
          ref={quillRef}
          value={value}
          onChange={onChange}
          modules={modules}
          formats={formats}
          theme="snow"
          placeholder={placeholder}
          style={{
            minHeight: minHeight,
            fontSize: '16px',
            fontFamily: 'Montserrat, sans-serif'
          }}
          {...props}
        />
        
        {/* Text Color Picker */}
        {showTextColorPicker && (
          <div
            ref={textColorRef}
            className="absolute z-50 mt-2 p-3 bg-white border border-gray-300 rounded-lg shadow-lg"
            style={{ top: '45px', left: '180px' }}
          >
            <div className="space-y-3">
              <div className="text-sm font-medium text-gray-700">Text Color</div>
              <HexColorPicker color={textColor} onChange={handleTextColorChange} />
              <div className="space-y-2">
                <label className="text-xs text-gray-600 block">Hex Code:</label>
                <input
                  type="text"
                  value={textColorHex}
                  onChange={handleTextColorHexChange}
                  onKeyPress={(e) => handleHexKeyPress(e, true)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="#000000"
                  maxLength={7}
                />
              </div>
            </div>
          </div>
        )}

        {/* Background Color Picker */}
        {showBackgroundColorPicker && (
          <div
            ref={backgroundColorRef}
            className="absolute z-50 mt-2 p-3 bg-white border border-gray-300 rounded-lg shadow-lg"
            style={{ top: '45px', left: '220px' }}
          >
            <div className="space-y-3">
              <div className="text-sm font-medium text-gray-700">Background Color</div>
              <HexColorPicker color={backgroundColor} onChange={handleBackgroundColorChange} />
              <div className="space-y-2">
                <label className="text-xs text-gray-600 block">Hex Code:</label>
                <input
                  type="text"
                  value={backgroundColorHex}
                  onChange={handleBackgroundColorHexChange}
                  onKeyPress={(e) => handleHexKeyPress(e, false)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="#ffff00"
                  maxLength={7}
                />
              </div>
            </div>
          </div>
        )}
      </div>

     
    </>
  );
};

export default Editor;